import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './PlannerAdmin.css';

interface Stage {
  id: number;
  stage_name: string;
  week_start: number;
  week_end: number;
  feed_product_name: string;
  lbs_per_animal: number;
}

interface Profile {
  id: number;
  animal_type: string;
  display_name: string;
  category: string;
  category_display: string;
  production_system: string;
  production_system_display: string;
  description: string;
  total_weeks: number;
  total_feed_lbs: number;
  is_recurring: boolean;
  is_active: boolean;
  stage_count: number;
  stages?: Stage[];
}

const FeedProfilesAdmin: React.FC = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedProfile, setExpandedProfile] = useState<number | null>(null);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, [categoryFilter, activeFilter]);

  const fetchProfiles = async () => {
    try {
      const params: Record<string, string> = {};
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (activeFilter !== 'all') params.active_only = activeFilter === 'active' ? 'true' : 'false';

      const response = await api.get('/planner/admin/feed-profiles/', { params });
      setProfiles(response.data);
    } catch (error) {
      console.error('Failed to load profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfileDetail = async (profileId: number) => {
    try {
      const response = await api.get(`/planner/admin/feed-profiles/${profileId}/`);
      // Update the profile in the list with full details
      setProfiles(prev => prev.map(p => p.id === profileId ? response.data : p));
      return response.data;
    } catch (error) {
      console.error('Failed to load profile detail:', error);
    }
  };

  const handleExpandProfile = async (profileId: number) => {
    if (expandedProfile === profileId) {
      setExpandedProfile(null);
    } else {
      setExpandedProfile(profileId);
      // Fetch full details if not already loaded
      const profile = profiles.find(p => p.id === profileId);
      if (profile && !profile.stages) {
        await fetchProfileDetail(profileId);
      }
    }
  };

  const handleToggleActive = async (profileId: number, currentState: boolean) => {
    try {
      await api.patch(`/planner/admin/feed-profiles/${profileId}/`, {
        is_active: !currentState
      });
      fetchProfiles();
    } catch (error) {
      console.error('Failed to toggle profile status:', error);
    }
  };

  const handleDuplicate = async (profileId: number) => {
    const newName = window.prompt('Enter a name for the duplicated profile:');
    if (!newName) return;

    try {
      await api.post(`/planner/admin/feed-profiles/${profileId}/duplicate/`, { name: newName });
      fetchProfiles();
    } catch (error) {
      console.error('Failed to duplicate profile:', error);
      alert('Failed to duplicate profile');
    }
  };

  if (loading) {
    return <div className="planner-admin-loading">Loading feed profiles...</div>;
  }

  return (
    <div className="profiles-admin-page">
      <div className="page-header">
        <h1>Feed Consumption Profiles</h1>
        <div>
          <Link to="/planner" className="btn-secondary" style={{ marginRight: '12px' }}>
            ← Back
          </Link>
          <button className="btn-primary" onClick={() => setShowCreateForm(true)}>
            + Create Profile
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="all">All Categories</option>
          <option value="poultry_meat">Poultry - Meat Birds</option>
          <option value="poultry_layer">Poultry - Layers</option>
          <option value="swine">Swine</option>
          <option value="goat">Goat</option>
          <option value="sheep">Sheep</option>
          <option value="rabbit">Rabbit</option>
          <option value="cattle">Cattle</option>
          <option value="equine">Equine</option>
        </select>
        <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
          <option value="all">All Profiles</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>
      </div>

      <div className="profiles-list">
        {profiles.map(profile => (
          <div key={profile.id} className={`profile-card ${!profile.is_active ? 'inactive' : ''}`}>
            <div className="profile-header" onClick={() => handleExpandProfile(profile.id)}>
              <div className="profile-main-info">
                <h3>{profile.display_name}</h3>
                <span className="profile-category">{profile.category_display}</span>
                <span className="profile-system">{profile.production_system_display}</span>
              </div>
              <div className="profile-stats">
                <span className="stat">
                  <strong>{profile.total_weeks || 'Ongoing'}</strong> weeks
                </span>
                <span className="stat">
                  <strong>{profile.total_feed_lbs || '-'}</strong> lbs/animal
                </span>
                <span className="stat">
                  <strong>{profile.stage_count}</strong> stages
                </span>
                <span className={`status-badge ${profile.is_active ? 'active' : 'inactive'}`}>
                  {profile.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="expand-icon">
                {expandedProfile === profile.id ? '▼' : '▶'}
              </div>
            </div>

            {expandedProfile === profile.id && (
              <div className="profile-details">
                {profile.description && (
                  <p className="profile-description">{profile.description}</p>
                )}

                <div className="stages-section">
                  <h4>Feed Stages</h4>
                  {profile.stages && profile.stages.length > 0 ? (
                    <table className="stages-table">
                      <thead>
                        <tr>
                          <th>Stage</th>
                          <th>Weeks</th>
                          <th>Feed Product</th>
                          <th>Lbs/Animal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profile.stages.map(stage => (
                          <tr key={stage.id}>
                            <td>{stage.stage_name}</td>
                            <td>{stage.week_start} - {stage.week_end}</td>
                            <td>{stage.feed_product_name || 'Not assigned'}</td>
                            <td>{stage.lbs_per_animal}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="no-stages">No feed stages configured</p>
                  )}
                </div>

                <div className="profile-actions">
                  <button
                    className="btn-small edit"
                    onClick={() => setEditingProfile(profile)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-small"
                    onClick={() => handleDuplicate(profile.id)}
                  >
                    Duplicate
                  </button>
                  <button
                    className={`btn-small ${profile.is_active ? 'warning' : 'success'}`}
                    onClick={() => handleToggleActive(profile.id, profile.is_active)}
                  >
                    {profile.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {profiles.length === 0 && (
          <p className="empty-message">No profiles found matching your filters</p>
        )}
      </div>

      {/* Create/Edit Modal would go here - simplified for now */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Create Feed Profile</h2>
            <p>Profile creation form coming soon. Use the Django admin for now.</p>
            <button className="btn-secondary" onClick={() => setShowCreateForm(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {editingProfile && (
        <div className="modal-overlay" onClick={() => setEditingProfile(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Edit Profile: {editingProfile.display_name}</h2>
            <p>Profile editing form coming soon. Use the Django admin for now.</p>
            <button className="btn-secondary" onClick={() => setEditingProfile(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedProfilesAdmin;
